'use client'

import { Skeleton } from '@lastbrand/web-ui/skeleton'
import { cx } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useRef } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { getExpressCheckoutSrc } from '@src/quince-components/src/atoms/express-checkout/express-checkout.helpers'
import { Loader } from '@src/quince-components/src/atoms/loaderWrapper/Loader'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import {
  containerCss,
  iframeCss,
  loaderContainerCss,
  loaderSkeletonCss,
  loaderSkeletonSecondaryCss,
  paymentOverlayCss,
  paymentOverlayDescriptionCss,
  paymentOverlayTitleCss,
} from './express-checkout-ds-styles'

export interface ExpressCheckoutProps {
  showBothButtons: boolean
  showPaymentLoader?: boolean
  externalRef: React.MutableRefObject<HTMLIFrameElement | null>
  className?: string
  containerClass?: string
  showLoader?: boolean
}

export function ExpressCheckoutDS({
  showBothButtons,
  showPaymentLoader = false,
  externalRef,
  className,
  containerClass,
  showLoader = false,
}: ExpressCheckoutProps) {
  const { t } = useTranslation('common')
  const { locale } = useLocaleContext()
  const deviceInfo = useDeviceInfo()
  const isI18nPreviewEnabled = useI18nPreviewContext()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const { src: iframeSrc } = getExpressCheckoutSrc(isI18nPreviewEnabled, showBothButtons, isMobile, locale)
  const ref = useRef<HTMLIFrameElement | null>(null)

  if (!iframeSrc) return null

  return (
    <div className={cx(containerCss, containerClass)} data-dd-action-name="express checkout">
      {showLoader && (
        <div className={loaderContainerCss}>
          <Skeleton css={loaderSkeletonCss} aria-hidden />
          {showBothButtons ? <Skeleton css={loaderSkeletonSecondaryCss} aria-hidden /> : null}
        </div>
      )}
      {showPaymentLoader ? (
        <div className={paymentOverlayCss} data-testid="page-loader">
          <div>
            <div className={paymentOverlayTitleCss}>{t('payment_processing')}</div>
            <div className={paymentOverlayDescriptionCss}>{t('payment_processing_warning')}</div>
            <Loader showLoader />
          </div>
        </div>
      ) : null}
      <iframe
        title={t('express_checkout_iframe')}
        ref={el => {
          ref.current = el
          externalRef.current = el
        }}
        src={iframeSrc}
        className={cx(iframeCss, className)}
        data-testid="express-checkout-iframe"
      />
    </div>
  )
}
